import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { useProduto } from '@/hooks/consultas';
import { classificacao, formatarDimensoes, formatarPeso, formatarVolume, nomeExibido } from '@/lib/produto';
import { Badge, Esqueleto, LinhasEsqueleto } from '@/componentes/ui/primitivos';
import { EstadoErro } from '@/componentes/ui/estados';
import { Gaveta } from '@/componentes/ui/gaveta';
import { GaleriaProduto } from './GaleriaProduto';

/**
 * Ficha técnica completa — em gaveta, não em tela nova (regra 2 do blueprint).
 *
 * Imagem e atributos são independentes: existe SKU com cinco fotos e nenhum
 * atributo (11314) e SKU com ficha completa e nenhuma foto (32991). Cada
 * ausência é dita no seu próprio lugar.
 */
export function DrawerProduto({
  sku,
  aoFechar,
}: {
  sku: number | null;
  aoFechar: () => void;
}): JSX.Element {
  const consulta = useProduto(sku);
  const produto = consulta.data ?? null;

  return (
    <Gaveta
      aberta={sku !== null}
      aoFechar={aoFechar}
      titulo={produto ? nomeExibido(produto, `Produto ${produto.sku}`) : `Produto ${sku ?? ''}`}
      subtitulo={produto ? `SKU ${produto.sku}` : undefined}
    >
      <div className="space-y-4 p-4">
        {consulta.isLoading ? (
          <>
            <Esqueleto className="h-44 w-full" />
            <LinhasEsqueleto linhas={5} />
          </>
        ) : consulta.isError ? (
          <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
        ) : produto ? (
          <>
            <GaleriaProduto
              sku={produto.sku}
              candidatas={produto.imagens}
              alt={nomeExibido(produto, `Produto ${produto.sku}`)}
            />

            {produto.descricao && produto.descricao !== produto.nome ? (
              <p className="text-sm text-textoFraco">{produto.descricao}</p>
            ) : null}

            {classificacao(produto).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {classificacao(produto).map((parte) => (
                  <Badge key={parte} className="border-borda bg-superficie2 text-textoFraco">
                    {parte}
                  </Badge>
                ))}
              </div>
            ) : null}

            <Secao titulo="Identificação">
              <Linha rotulo="SKU" valor={String(produto.sku)} mono />
              <Linha rotulo="EAN" valor={produto.ean} mono />
              <Linha rotulo="Part number" valor={produto.partNumber} mono />
              <Linha rotulo="NCM" valor={produto.ncm} mono />
            </Secao>

            <Secao titulo="Medidas">
              <Linha rotulo="Peso bruto" valor={formatarPeso(produto.pesoBrutoKg)} />
              <Linha rotulo="Dimensões (A × L × P)" valor={formatarDimensoes(produto)} />
              <Linha rotulo="Volume" valor={formatarVolume(produto.volumeM3)} />
            </Secao>

            {!produto.temAtributos ? (
              <p className="flex items-start gap-2 rounded-lg border border-borda bg-superficie2 px-3 py-2 text-xs text-textoFraco">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Este SKU não tem cadastro de atributos no PBS — só a descrição. Não é erro de
                carregamento: os campos acima que aparecem como “—” simplesmente não existem.
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </Gaveta>
  );
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }): JSX.Element {
  return (
    <section>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-textoFraco">{titulo}</h3>
      <dl className="divide-y divide-borda rounded-lg border border-borda">{children}</dl>
    </section>
  );
}

function Linha({
  rotulo,
  valor,
  mono = false,
}: {
  rotulo: string;
  valor: string | null | undefined;
  mono?: boolean | undefined;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm">
      <dt className="text-textoFraco">{rotulo}</dt>
      <dd className={mono ? 'font-mono' : undefined}>{valor && valor !== '—' ? valor : '—'}</dd>
    </div>
  );
}
